import { type SanityImageSource } from "@sanity/image-url/lib/types/types";
import { groq } from "next-sanity";
import { getClient } from "../sanity";
import { type Customer, customerFragment } from "./customers";

export type CaseStudy = {
  title: string;
  image: SanityImageSource;
  excerpt?: string;
  content: string;
  customer: Customer;
  featuredQuote?: string;
  featuredCustomerImage?: string;
  featuredCustomerName?: string;
  _updatedAt: string;
  metaTitle?: string;
  metaDescription?: string;
  metaImageUrl?: string;
};

export const getCaseStudy = async (slug: string): Promise<CaseStudy> => {
  const query = groq`
    *[_type == "caseStudy" && customer->slug == "${slug}"] {
      title,
      image,
      excerpt,
      content,
      customer->{ ${customerFragment} },
      featuredQuote,
      'featuredCustomerImage': featuredCustomerImage.asset->url,
      featuredCustomerName,
      _updatedAt,
      metaTitle,
      metaDescription,
      'metaImageUrl': metaImage.asset->url,
    } | order(_updatedAt desc)
  `;

  const caseStudies = await getClient().fetch(query);
  return caseStudies[0];
};

export type CaseStudySimple = {
  title: string;
  customerSlug: string;
  customerLogo: string;
  customerLogoMono?: string;
  customerName: string;
  imageUrl: string;
  featuredQuote?: string;
  featuredCustomerImage?: string;
  featuredCustomerName?: string;
  _updatedAt?: string;
};

const caseStudySimpleFragment = `
  title,
  'customerSlug': customer->slug,
  'customerLogo': customer->logo.asset->url,
  'customerLogoMono': customer->logoMonotone.asset->url,
  'customerName': customer->name,
  'imageUrl': image.asset->url,
  featuredQuote,
  'featuredCustomerImage': featuredCustomerImage.asset->url,
  featuredCustomerName, _updatedAt
  `;

export const getSimpleCaseStudy = async (slug: string): Promise<CaseStudySimple> => {
  const query = groq`
    *[_type == "caseStudy" && customer->slug == "${slug}"] {
      ${caseStudySimpleFragment}
    }
  `;

  const caseStudies = await getClient().fetch(query);
  return caseStudies?.[0];
};

export const getSimpleCaseStudies = async (slugs?: string[]): Promise<CaseStudySimple[]> => {
  const query = groq`
    *[_type == "caseStudy" 
      ${slugs ? `&& customer->slug in [${slugs.map((slug) => "'" + slug + "'")}]` : ""}
    ] {
      ${caseStudySimpleFragment}
    } | order(_updatedAt desc)
  `;

  const caseStudies = await getClient().fetch<CaseStudySimple[]>(query);
  return caseStudies;
};
